
import React from 'react';
import { User, RestaurantConfig, UserRole } from '../../types';
import { Trophy, Settings, ListOrdered, Grid, LogOut, Calendar, History, Sparkles } from 'lucide-react';

interface LayoutProps {
  config: RestaurantConfig;
  currentUser: User | null;
  isAdmin: boolean;
  isLoggedIn: boolean;
  adminView: string;
  setAdminView: (view: any) => void;
  adminNotificationCount: number;
  onLogout: () => void;
  onShowSettings: () => void;
  children: React.ReactNode;
}

export const DemoLayout: React.FC<LayoutProps> = ({
  config,
  currentUser,
  isAdmin,
  isLoggedIn,
  adminView,
  setAdminView,
  adminNotificationCount,
  onLogout,
  onShowSettings,
  children
}) => {
  const isDarkMode = config.theme?.darkMode;
  const primaryColor = config.theme?.primaryColor || '#10b981';
  const secondaryColor = config.theme?.secondaryColor || '#064e3b';

  const styleVars = `
    :root {
      --color-primary: ${primaryColor};
      --color-secondary: ${secondaryColor};
      --color-primary-light: ${primaryColor}20;
      --color-calendar-confirmed: ${config.theme?.calendarConfirmedColor || secondaryColor};
      --color-calendar-pending: ${config.theme?.calendarPendingColor || '#f97316'};
    }
    
    body {
      background-color: ${isLoggedIn ? (isDarkMode ? '#0f172a' : '#f8fafc') : '#020617'};
      color: ${isLoggedIn ? (isDarkMode ? '#e2e8f0' : '#0f172a') : '#ffffff'};
      transition: background-color 0.2s ease;
    }

    ${isDarkMode ? `
      header { background-color: #111827 !important; border-bottom-color: #1f2937 !important; color: #f1f5f9 !important; }
      .bg-white { background-color: #1f2937 !important; color: #f1f5f9 !important; border-color: #374151 !important; }
    ` : ''}
    
    .theme-text-primary { color: var(--color-primary); }
    .theme-bg-primary { background-color: var(--color-primary); }
    .theme-bg-secondary { background-color: var(--color-secondary); }
    .theme-border-primary { border-color: var(--color-primary); }
  `;

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen flex flex-col font-sans bg-[#020617]">
        <style>{styleVars}</style>
        {children}
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex flex-col font-sans`}>
      <style>{styleVars}</style>
      
      <header className="bg-white border-b border-slate-200 px-4 md:px-8 py-4 flex flex-wrap md:flex-nowrap items-center justify-between sticky top-0 z-[80] shadow-sm animate-fade-in-down gap-y-2">
        <div className="flex items-center gap-3 order-1">
          {config.logoUrl ? (
            <div className="relative">
              <img src={config.logoUrl} alt="Logo" className="w-10 h-10 object-contain rounded-xl" />
              <div className="absolute -top-1 -right-1 bg-amber-500 text-white rounded-full p-0.5 shadow-sm">
                <Sparkles size={8} />
              </div>
            </div>
          ) : (
            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white theme-bg-primary shadow-lg shadow-emerald-500/20 rotate-3 transition-transform hover:rotate-0 relative">
                <Trophy size={20} />
                <div className="absolute -top-1 -right-1 bg-amber-500 text-white rounded-full p-0.5 shadow-sm">
                  <Sparkles size={8} />
                </div>
            </div>
          )}
          <div>
            <div className="flex items-center gap-1.5">
              <h1 className="font-black text-sm md:text-base text-slate-800 tracking-tighter uppercase italic">{config.name}</h1>
              <span className="bg-amber-100 text-amber-700 text-[8px] font-black px-1.5 py-0.5 rounded uppercase tracking-widest border border-amber-200">Demo</span>
            </div>
            {isAdmin && <span className="text-[10px] font-black theme-text-primary uppercase tracking-widest leading-none block mt-0.5">Control Center</span>}
          </div>
        </div>

        {isAdmin && (
          <div className={`flex order-3 md:order-2 w-full md:w-auto justify-center mt-2 md:mt-0 p-1 rounded-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-slate-100'}`}>
            <button
              onClick={() => setAdminView('EDITOR')}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
                adminView === 'EDITOR' ? 'bg-white theme-text-primary shadow-md' : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              <Grid size={14} /> <span className="hidden lg:inline">Überblick</span>
            </button>
            <button
              onClick={() => setAdminView('RESERVATIONS')}
              className={`relative flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
                adminView === 'RESERVATIONS' ? 'bg-white theme-text-primary shadow-md' : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              <ListOrdered size={14} /> <span className="hidden lg:inline">Buchungen</span>
              {adminNotificationCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white animate-pulse flex items-center justify-center text-[8px] text-white font-bold">{adminNotificationCount}</span>
              )}
            </button>
            <button
              onClick={() => setAdminView('CALENDAR')}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
                adminView === 'CALENDAR' ? 'bg-white theme-text-primary shadow-md' : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              <Calendar size={14} /> <span className="hidden lg:inline">Kalender</span>
            </button>
            <button
              onClick={() => setAdminView('HISTORY')}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
                adminView === 'HISTORY' ? 'bg-white theme-text-primary shadow-md' : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              <History size={14} /> <span className="hidden lg:inline">Historie</span>
            </button>
          </div>
        )}

        <div className="flex gap-2 md:gap-4 items-center order-2 md:order-3 ml-auto md:ml-0">
          {isAdmin && (
            <button 
              onClick={onShowSettings}
              className="flex items-center justify-center w-10 h-10 rounded-xl text-slate-500 hover:bg-slate-100 transition-all border border-slate-200"
              title="Einstellungen"
            >
              <Settings size={20} /> 
            </button>
          )}

          <div className="flex items-center gap-4 pl-4 border-l border-slate-200">
            <div className="text-right hidden sm:block">
                <div className="text-xs font-black text-slate-800 uppercase tracking-tight">
                  {currentUser ? currentUser.name : 'Gast-Besucher'}
                </div>
                <div className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                  {isAdmin ? 'Administrator' : currentUser ? 'Arena Member' : 'Einfache Buchung'}
                </div>
            </div>
            <button 
                onClick={onLogout}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all shadow-lg shadow-black/10 ${isDarkMode ? 'bg-gray-800 text-red-400 hover:bg-gray-700' : 'theme-bg-secondary text-white hover:opacity-90'}`}
            >
                <LogOut size={16} /> <span className="hidden md:inline">{currentUser ? 'Logout' : 'Beenden'}</span>
            </button>
          </div>
        </div>
      </header>

      <main className={`flex-1 relative ${adminView === 'EDITOR' && isAdmin ? 'overflow-hidden' : 'overflow-auto'} ${isDarkMode ? 'bg-gray-950 p-4 md:p-10' : 'bg-slate-50 p-4 md:p-10'}`}>
        {children}
      </main>
    </div>
  );
};
